import { useState } from 'react';
import { Send, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) return;

    setStatus('loading');
    
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      setStatus('success');
      setMessage(response.data.message);
      if (!response.data.already_subscribed) {
        setEmail('');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Oops! Something went wrong. Please try again.');
    }

    // Reset after 4 seconds
    setTimeout(() => {
      setStatus('idle');
      setMessage('');
    }, 4000);
  };

  return (
    <section
      data-testid="newsletter-section"
      className="py-24 bg-[#1e3a5f] relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-40 h-40 rounded-full border-2 border-white" />
        <div className="absolute bottom-10 right-10 w-60 h-60 rounded-full border-2 border-[#c68b4d]" />
        <div className="absolute top-1/2 right-1/4 w-32 h-32 rounded-full border-2 border-[#e76f51]" />
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          {/* Header */}
          <span className="font-accent text-3xl text-[#c68b4d] block mb-4">Stay Connected</span>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-white mb-4">
            Join the Crew
          </h2>
          <p className="font-body text-lg leading-relaxed text-white/70 mb-8">
            Be the first to know about new arrivals, exclusive deals, and cute pet content. No spam, just good vibes.
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="relative flex-1 max-w-md">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email-input"
                className="w-full h-14 bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-full px-6 focus:border-[#c68b4d] focus:ring-[#c68b4d]"
                disabled={status === 'loading' || status === 'success'}
              />
            </div>
            <Button
              type="submit"
              data-testid="newsletter-submit-btn"
              disabled={status === 'loading' || status === 'success'}
              className={`h-14 px-8 rounded-full font-semibold transition-all ${
                status === 'success'
                  ? 'bg-green-500 hover:bg-green-500'
                  : 'bg-[#e76f51] hover:bg-[#e76f51]/90'
              } text-white shadow-lg hover:shadow-xl`}
            >
              {status === 'loading' ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Subscribing...
                </>
              ) : status === 'success' ? (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Subscribed!
                </>
              ) : (
                <>
                  Subscribe
                  <Send className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Message */}
          {message && (
            <p
              data-testid="newsletter-message"
              className={`mt-4 font-body text-sm ${
                status === 'success' ? 'text-green-400' : 'text-red-400'
              }`}
            >
              {message}
            </p>
          )}

          {/* Trust text */}
          <p className="mt-6 font-body text-sm text-white/50">
            Join 8,000+ subscribers. Unsubscribe anytime.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;
